import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../lib/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import AdminHeader from '../components/AdminHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ArrowLeft, Search, Key, Ban, Copy, Check } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export default function AdminLicenses() {
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [revokedFilter, setRevokedFilter] = useState('all');
  const [licenseToRevoke, setLicenseToRevoke] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    if (!auth.isAuthenticated() || !auth.getUser()?.is_admin) {
      navigate('/login');
      return;
    }

    loadLicenses();
  }, [navigate, tierFilter, revokedFilter]);

  const loadLicenses = async () => {
    try {
      setLoading(true);
      setError('');

      const token = auth.getToken();
      const params = new URLSearchParams();
      
      if (tierFilter !== 'all') params.append('tier', tierFilter);
      if (revokedFilter !== 'all') params.append('revoked', revokedFilter === 'true');
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/licenses?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLicenses(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadLicenses();
  };

  const handleRevokeLicense = async () => {
    if (!licenseToRevoke || !revokeReason.trim()) return;

    try {
      setRevoking(true);
      const token = auth.getToken();
      
      await axios.post(
        `${API_BASE_URL}/api/admin/licenses/${licenseToRevoke.id}/revoke`,
        { reason: revokeReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Reload licenses
      await loadLicenses();
      
      // Close dialog
      setLicenseToRevoke(null);
      setRevokeReason('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to revoke license');
    } finally {
      setRevoking(false);
    }
  };

  const copyLicenseKey = (licenseKey, licenseId) => {
    navigator.clipboard.writeText(licenseKey);
    setCopiedKey(licenseId);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-12 w-64 mb-6 bg-gray-800" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-32 bg-gray-800" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Admin Header */}
      <AdminHeader />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <Alert variant="destructive" className="mb-6 bg-red-900/50 border-red-500/50 text-red-200">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card className="mb-6 bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
          <CardHeader>
            <CardTitle className="text-white">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="flex gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by email or license key..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-gray-800/50 border-purple-500/30 text-white placeholder:text-gray-400"
                />
              </div>

              <Select value={tierFilter} onValueChange={setTierFilter}>
                <SelectTrigger className="w-40 bg-gray-800/50 border-purple-500/30 text-white">
                  <SelectValue placeholder="Tier" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-purple-500/30">
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>

              <Select value={revokedFilter} onValueChange={setRevokedFilter}>
                <SelectTrigger className="w-40 bg-gray-800/50 border-purple-500/30 text-white">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-purple-500/30">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="false">Active</SelectItem>
                  <SelectItem value="true">Revoked</SelectItem>
                </SelectContent>
              </Select>

              <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* License List */}
        <div className="space-y-4">
          {licenses.length === 0 ? (
            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardContent className="py-12">
                <div className="text-center">
                  <Key className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-white">No licenses found</h3>
                  <p className="mt-1 text-sm text-gray-400">
                    Try adjusting your filters or create a new license.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            licenses.map((license) => (
              <Card key={license.id} className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={license.revoked ? 'destructive' : 'default'} className={license.revoked ? '' : 'bg-purple-600'}>
                          {license.tier.toUpperCase()}
                        </Badge>
                        {license.revoked && (
                          <Badge variant="destructive">REVOKED</Badge>
                        )}
                        {!license.revoked && !license.expires_at && (
                          <Badge variant="secondary" className="bg-gray-700 text-gray-200">LIFETIME</Badge>
                        )}
                      </div>

                      <div className="font-mono text-xs bg-gray-800/50 p-2 rounded flex items-center justify-between mb-3 text-gray-200">
                        <span className="truncate max-w-[600px]" title={license.license_key}>
                          {license.license_key.substring(0, 60)}...
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyLicenseKey(license.license_key, license.id)}
                          className="hover:bg-gray-700 flex-shrink-0"
                        >
                          {copiedKey === license.id ? (
                            <Check className="h-4 w-4 text-green-400" />
                          ) : (
                            <Copy className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-300">
                        <div>
                          <span className="font-medium text-purple-400">Email:</span> {license.email}
                        </div>
                        <div>
                          <span className="font-medium text-purple-400">Issued:</span> {formatDate(license.issued_at)}
                        </div>
                        <div>
                          <span className="font-medium text-purple-400">Devices:</span> {license.active_devices}/{license.device_limit}
                        </div>
                        {license.expires_at && (
                          <div>
                            <span className="font-medium text-purple-400">Expires:</span> {formatDate(license.expires_at)}
                          </div>
                        )}
                      </div>

                      {license.revoked && license.revoked_reason && (
                        <div className="mt-2 text-sm text-red-400">
                          <span className="font-medium">Revoked:</span> {license.revoked_reason}
                        </div>
                      )}
                    </div>

                    <div className="ml-4">
                      {!license.revoked && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setLicenseToRevoke(license)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          <Ban className="h-4 w-4 mr-2" />
                          Revoke
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog open={!!licenseToRevoke} onOpenChange={() => setLicenseToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke License</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this license for {licenseToRevoke?.email}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <Input
              placeholder="Reason for revocation..."
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              disabled={revoking}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeLicense}
              disabled={revoking || !revokeReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {revoking ? 'Revoking...' : 'Revoke License'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

